/**
 * 足球战术演示效果
 * 
 * 基于足球战术可视化常见模式自主实现
 * 
 * 核心功能：
 * 1. 标准足球场地（11 人制）
 * 2. 球员位置动画（两队 22 人）
 * 3. 跑位轨迹（虚线 + 箭头）
 * 4. 传球路线（动态箭头）
 * 5. 战术标注（号码 + 位置）
 * 
 * 技术亮点：
 * - SVG 球场绘制（符合 FIFA 标准）
 * - Spring 平滑动画
 * - 数据驱动可视化
 * - 模块化设计
 * 
 * @author 龙虾菌 🦞
 * @date 2026-03-09
 */

import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

// ==================== 类型定义 ====================

/**
 * 球员数据结构
 */
interface Player {
  id: number;
  number: number; // 球衣号码
  name: string;
  position: 'GK' | 'DEF' | 'MID' | 'FWD'; // 门将/后卫/中场/前锋
  startCoords: [number, number]; // 起始坐标 [x, y] (0-100)
  endCoords?: [number, number]; // 结束坐标（跑位）
  team: 'home' | 'away';
}

/**
 * 传球数据结构
 */
interface Pass {
  from: number; // 球员 ID
  to: number; // 球员 ID
  type: 'short' | 'long' | 'cross'; // 短传/长传/传中
}

// ==================== 球场组件 ====================

/**
 * 足球场地 SVG
 * 
 * 标准 11 人制球场尺寸：
 * - 长度：100-110 米（本设计使用 105 米）
 * - 宽度：64-75 米（本设计使用 68 米）
 * 
 * 球场元素：
 * - 边线、底线
 * - 中圈、中点
 * - 禁区、小禁区
 * - 点球点
 * - 角球区
 */
const FootballPitch: React.FC = () => {
  return (
    <svg
      viewBox="0 0 105 68"
      preserveAspectRatio="none"
      style={{
        width: '100%',
        height: '100%',
      }}
    >
      {/* 草地背景 */}
      <rect x="0" y="0" width="105" height="68" fill="#2d5016" />
      
      {/* 草地条纹 */}
      {[...Array(12)].map((_, i) => (
        <rect
          key={i}
          x={i * 8.75}
          y="0"
          width="8.75"
          height="68"
          fill={i % 2 === 0 ? '#2d5016' : '#336619'}
        />
      ))}
      
      {/* 球场边线 */}
      <rect
        x="0"
        y="0"
        width="105"
        height="68"
        fill="none"
        stroke="white"
        strokeWidth="0.3"
      />
      
      {/* 中线 */}
      <line
        x1="52.5"
        y1="0"
        x2="52.5"
        y2="68"
        stroke="white"
        strokeWidth="0.3"
      />
      
      {/* 中圈 */}
      <circle
        cx="52.5"
        cy="34"
        r="9.15"
        fill="none"
        stroke="white"
        strokeWidth="0.3"
      />
      
      {/* 中点 */}
      <circle cx="52.5" cy="34" r="0.4" fill="white" />
      
      {/* 左侧禁区（16.5m x 40.32m） */}
      <rect
        x="0"
        y="13.84"
        width="16.5"
        height="40.32"
        fill="none"
        stroke="white"
        strokeWidth="0.3"
      />
      
      {/* 左侧小禁区（5.5m x 18.32m） */}
      <rect
        x="0"
        y="24.84"
        width="5.5"
        height="18.32"
        fill="none"
        stroke="white"
        strokeWidth="0.3"
      />
      
      {/* 左侧点球点（距离球门 11m） */}
      <circle cx="11" cy="34" r="0.3" fill="white" />
      
      {/* 左侧禁区弧 */}
      <path
        d="M 16.5 25.84 A 9.15 9.15 0 0 1 16.5 42.16"
        fill="none"
        stroke="white"
        strokeWidth="0.3"
      />
      
      {/* 右侧禁区（16.5m x 40.32m） */}
      <rect
        x="88.5"
        y="13.84"
        width="16.5"
        height="40.32"
        fill="none"
        stroke="white"
        strokeWidth="0.3"
      />
      
      {/* 右侧小禁区（5.5m x 18.32m） */}
      <rect
        x="99.5"
        y="24.84"
        width="5.5"
        height="18.32"
        fill="none"
        stroke="white"
        strokeWidth="0.3"
      />
      
      {/* 右侧点球点（距离球门 11m） */}
      <circle cx="94" cy="34" r="0.3" fill="white" />
      
      {/* 右侧禁区弧 */}
      <path
        d="M 88.5 25.84 A 9.15 9.15 0 0 0 88.5 42.16"
        fill="none"
        stroke="white"
        strokeWidth="0.3"
      />
      
      {/* 角球区（4个角） */}
      {[
        { cx: 0, cy: 0, startAngle: 0 },
        { cx: 105, cy: 0, startAngle: 270 },
        { cx: 0, cy: 68, startAngle: 90 },
        { cx: 105, cy: 68, startAngle: 180 },
      ].map((corner, i) => (
        <path
          key={i}
          d={`M ${corner.cx} ${corner.cy - 1} A 1 1 0 0 ${corner.startAngle < 180 ? 0 : 1} ${corner.cx + (corner.cx === 0 ? 1 : -1)} ${corner.cy}`}
          fill="none"
          stroke="white"
          strokeWidth="0.3"
        />
      ))}
      
      {/* 球门（左侧） */}
      <rect
        x="-1.5"
        y="30.34"
        width="1.5"
        height="7.32"
        fill="none"
        stroke="white"
        strokeWidth="0.3"
      />
      
      {/* 球门（右侧） */}
      <rect
        x="105"
        y="30.34"
        width="1.5"
        height="7.32"
        fill="none"
        stroke="white"
        strokeWidth="0.3"
      />
    </svg>
  );
};

// ==================== 球员组件 ====================

/**
 * 单个球员标记
 * 
 * 显示：
 * - 球员位置（圆点）
 * - 球衣号码
 * - 位置标签
 * - 跑位轨迹（可选）
 */
const PlayerMarker: React.FC<{
  player: Player;
  frame: number;
  fps: number;
  showTrail?: boolean;
  delay?: number;
}> = ({ player, frame, fps, showTrail = false, delay = 0 }) => {
  const adjustedFrame = Math.max(0, frame - delay);
  
  // 入场动画（Spring）
  const scale = spring({
    frame: adjustedFrame,
    fps,
    config: {
      damping: 15,
      stiffness: 100,
    },
  });
  
  // 透明度动画
  const opacity = interpolate(adjustedFrame, [0, 10], [0, 1], {
    extrapolateRight: 'clamp',
  });
  
  // 跑位动画
  let currentX = player.startCoords[0];
  let currentY = player.startCoords[1];
  
  if (player.endCoords && adjustedFrame > 30) {
    const moveProgress = interpolate(adjustedFrame, [30, 90], [0, 1], {
      extrapolateRight: 'clamp',
    });
    currentX = interpolate(
      moveProgress,
      [0, 1],
      [player.startCoords[0], player.endCoords[0]]
    );
    currentY = interpolate(
      moveProgress,
      [0, 1],
      [player.startCoords[1], player.endCoords[1]]
    );
  }
  
  // 颜色配置
  const teamColor = player.team === 'home' ? '#ff4136' : '#0074d9'; // 红色 vs 蓝色
  const textColor = 'white';
  
  // 位置标签映射
  const positionLabels = {
    GK: '门将',
    DEF: '后卫',
    MID: '中场',
    FWD: '前锋',
  };
  
  return (
    <div
      style={{
        position: 'absolute',
        left: `${currentX}%`,
        top: `${currentY}%`,
        transform: `translate(-50%, -50%) scale(${scale})`,
        opacity,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      {/* 跑位轨迹 */}
      {showTrail && player.endCoords && adjustedFrame > 30 && (
        <svg
          style={{
            position: 'fixed',
            left: 0,
            top: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            overflow: 'visible',
          }}
        >
          <defs>
            <marker
              id={`arrow-${player.id}`}
              markerWidth="10"
              markerHeight="10"
              refX="9"
              refY="3"
              orient="auto"
              markerUnits="strokeWidth"
            >
              <path d="M0,0 L0,6 L9,3 z" fill={teamColor} opacity="0.6" />
            </marker>
          </defs>
          <line
            x1={`${player.startCoords[0]}%`}
            y1={`${player.startCoords[1]}%`}
            x2={`${currentX}%`}
            y2={`${currentY}%`}
            stroke={teamColor}
            strokeWidth="2"
            strokeDasharray="5,5"
            opacity="0.6"
            markerEnd={`url(#arrow-${player.id})`}
          />
        </svg>
      )}
      
      {/* 球员圆点 */}
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          backgroundColor: teamColor,
          border: '3px solid white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 18,
          fontWeight: 'bold',
          color: textColor,
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          zIndex: 10,
        }}
      >
        {player.number}
      </div>
      
      {/* 球员姓名 */}
      <div
        style={{
          marginTop: 4,
          padding: '2px 8px',
          backgroundColor: 'rgba(0,0,0,0.7)',
          borderRadius: 4,
          fontSize: 12,
          color: 'white',
          whiteSpace: 'nowrap',
        }}
      >
        {player.name}
      </div>
      
      {/* 位置标签 */}
      <div
        style={{
          marginTop: 2,
          padding: '1px 6px',
          backgroundColor: teamColor,
          borderRadius: 3,
          fontSize: 10,
          color: 'white',
          opacity: 0.8,
        }}
      >
        {positionLabels[player.position]}
      </div>
    </div>
  );
};

// ==================== 传球路线组件 ====================

/**
 * 传球路线动画
 * 
 * 显示：
 * - 传球起点和终点
 * - 动态箭头（虚线）
 * - 传球类型标签
 */
const PassLine: React.FC<{
  from: Player;
  to: Player;
  frame: number;
  fps: number;
  type: 'short' | 'long' | 'cross';
  delay: number;
}> = ({ from, to, frame, fps, type, delay }) => {
  const adjustedFrame = Math.max(0, frame - delay);
  
  // 入场动画
  const opacity = interpolate(adjustedFrame, [0, 10], [0, 1], {
    extrapolateRight: 'clamp',
  });
  
  // 路径绘制进度
  const progress = interpolate(adjustedFrame, [10, 40], [0, 1], {
    extrapolateRight: 'clamp',
  });
  
  // 当前球员位置
  const fromX = from.endCoords ? from.endCoords[0] : from.startCoords[0];
  const fromY = from.endCoords ? from.endCoords[1] : from.startCoords[1];
  const toX = to.endCoords ? to.endCoords[0] : to.startCoords[0];
  const toY = to.endCoords ? to.endCoords[1] : to.startCoords[1];
  
  // 传球类型颜色
  const passColors = {
    short: '#00ff00', // 绿色（短传）
    long: '#ffd700', // 金色（长传）
    cross: '#00f2ff', // 青色（传中）
  };
  
  // 传球类型标签
  const passLabels = {
    short: '短传',
    long: '长传',
    cross: '传中',
  };
  
  return (
    <svg
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        opacity,
      }}
    >
      <defs>
        <marker
          id={`pass-arrow-${from.id}-${to.id}`}
          markerWidth="10"
          markerHeight="10"
          refX="9"
          refY="3"
          orient="auto"
          markerUnits="strokeWidth"
        >
          <path
            d="M0,0 L0,6 L9,3 z"
            fill={passColors[type]}
          />
        </marker>
      </defs>
      
      {/* 传球路线（虚线） */}
      <line
        x1={`${fromX}%`}
        y1={`${fromY}%`}
        x2={`${fromX + (toX - fromX) * progress}%`}
        y2={`${fromY + (toY - fromY) * progress}%`}
        stroke={passColors[type]}
        strokeWidth="3"
        strokeDasharray="8,4"
        markerEnd={`url(#pass-arrow-${from.id}-${to.id})`}
        opacity="0.8"
      />
      
      {/* 传球类型标签 */}
      {progress > 0.5 && (
        <text
          x={`${(fromX + toX) / 2}%`}
          y={`${(fromY + toY) / 2 - 2}%`}
          fill={passColors[type]}
          fontSize="14"
          fontWeight="bold"
          textAnchor="middle"
          style={{
            textShadow: '0 1px 3px rgba(0,0,0,0.8)',
          }}
        >
          {passLabels[type]}
        </text>
      )}
    </svg>
  );
};

// ==================== 战术标题组件 ====================

/**
 * 战术标题和说明
 * 
 * 显示：
 * - 战术名称
 * - 战术说明
 * - 球队信息
 */
const TacticsTitle: React.FC<{
  frame: number;
  fps: number;
}> = ({ frame, fps }) => {
  const opacity = interpolate(frame, [0, 20], [0, 1], {
    extrapolateRight: 'clamp',
  });
  
  const translateY = interpolate(frame, [0, 20], [-30, 0], {
    extrapolateRight: 'clamp',
  });
  
  return (
    <div
      style={{
        position: 'absolute',
        top: 40,
        left: 0,
        right: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        opacity,
        transform: `translateY(${translateY}px)`,
      }}
    >
      {/* 战术名称 */}
      <div
        style={{
          padding: '10px 30px',
          backgroundColor: 'rgba(0,0,0,0.8)',
          borderRadius: 10,
          fontSize: 44,
          fontWeight: 'bold',
          color: 'white',
          textShadow: '0 2px 4px rgba(0,0,0,0.5)',
        }}
      >
        4-3-3 进攻战术
      </div>
      
      {/* 战术说明 */}
      <div
        style={{
          marginTop: 10,
          padding: '8px 20px',
          backgroundColor: 'rgba(0,0,0,0.6)',
          borderRadius: 8,
          fontSize: 32,
          color: '#cccccc'
        }}
      >
        边路突破 + 中路包抄 + 跑位拉扯
      </div>
      
      {/* 球队信息 */}
      <div
        style={{
          marginTop: 8,
          display: 'flex',
          gap: 40,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div
            style={{
              width: 16,
              height: 16,
              borderRadius: '50%',
              backgroundColor: '#ff4136',
            }}
          />
          <span style={{ color: 'white', fontSize: 24 }}>主队</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div
            style={{
              width: 16,
              height: 16,
              borderRadius: '50%',
              backgroundColor: '#0074d9',
            }}
          />
          <span style={{ color: 'white', fontSize: 24 }}>客队</span>
        </div>
      </div>
    </div>
  );
};

// ==================== 主组件 ====================

/**
 * 足球战术演示
 * 
 * Composition ID: FootballTactics
 * 分辨率: 1920x1080 (16:9)
 * 帧率: 30 FPS
 * 时长: 5 秒 (150 帧)
 */
export const FootballTactics: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  
  // 主队球员数据（4-3-3 阵型）
  const homePlayers: Player[] = [
    // 门将
    {
      id: 1,
      number: 1,
      name: '库尔图瓦',
      position: 'GK',
      startCoords: [5, 50],
      team: 'home',
    },
    // 后卫（4人）
    {
      id: 2,
      number: 2,
      name: '卡瓦哈尔',
      position: 'DEF',
      startCoords: [20, 18],
      endCoords: [30, 14],
      team: 'home',
    },
    {
      id: 3,
      number: 3,
      name: '米利唐',
      position: 'DEF',
      startCoords: [20, 38],
      team: 'home',
    },
    {
      id: 4,
      number: 4,
      name: '阿拉巴',
      position: 'DEF',
      startCoords: [20, 62],
      team: 'home',
    },
    {
      id: 5,
      number: 5,
      name: '门迪',
      position: 'DEF',
      startCoords: [20, 82],
      endCoords: [30, 86],
      team: 'home',
    },
    // 中场（3人）
    {
      id: 6,
      number: 6,
      name: '卡马文加',
      position: 'MID',
      startCoords: [38, 28],
      endCoords: [48, 22],
      team: 'home',
    },
    {
      id: 7,
      number: 7,
      name: '克罗斯',
      position: 'MID',
      startCoords: [35, 50],
      endCoords: [45, 50],
      team: 'home',
    },
    {
      id: 8,
      number: 8,
      name: '巴尔韦德',
      position: 'MID',
      startCoords: [38, 72],
      endCoords: [48, 78],
      team: 'home',
    },
    // 前锋（3人）
    {
      id: 9,
      number: 9,
      name: '维尼修斯',
      position: 'FWD',
      startCoords: [58, 18],
      endCoords: [72, 14],
      team: 'home',
    },
    {
      id: 10,
      number: 10,
      name: '本泽马',
      position: 'FWD',
      startCoords: [55, 50],
      endCoords: [68, 50],
      team: 'home',
    },
    {
      id: 11,
      number: 11,
      name: '罗德里戈',
      position: 'FWD',
      startCoords: [58, 82],
      endCoords: [72, 86],
      team: 'home',
    },
  ];
  
  // 客队球员数据（4-4-2 阵型）
  const awayPlayers: Player[] = [
    // 门将
    {
      id: 12,
      number: 1,
      name: '特尔施特根',
      position: 'GK',
      startCoords: [95, 50],
      team: 'away',
    },
    // 后卫（4人）
    {
      id: 13,
      number: 2,
      name: '罗贝托',
      position: 'DEF',
      startCoords: [80, 18],
      team: 'away',
    },
    {
      id: 14,
      number: 3,
      name: '阿劳霍',
      position: 'DEF',
      startCoords: [80, 38],
      team: 'away',
    },
    {
      id: 15,
      number: 4,
      name: '克里斯滕森',
      position: 'DEF',
      startCoords: [80, 62],
      team: 'away',
    },
    {
      id: 16,
      number: 5,
      name: '巴尔德',
      position: 'DEF',
      startCoords: [80, 82],
      team: 'away',
    },
    // 中场（4人）
    {
      id: 17,
      number: 6,
      name: '加维',
      position: 'MID',
      startCoords: [65, 22],
      team: 'away',
    },
    {
      id: 18,
      number: 7,
      name: '佩德里',
      position: 'MID',
      startCoords: [65, 40],
      team: 'away',
    },
    {
      id: 19,
      number: 8,
      name: '德容',
      position: 'MID',
      startCoords: [65, 60],
      team: 'away',
    },
    {
      id: 20,
      number: 9,
      name: '拉菲尼亚',
      position: 'MID',
      startCoords: [65, 78],
      team: 'away',
    },
    // 前锋（2人）
    {
      id: 21,
      number: 10,
      name: '莱万',
      position: 'FWD',
      startCoords: [52, 40],
      team: 'away',
    },
    {
      id: 22,
      number: 11,
      name: '费兰',
      position: 'FWD',
      startCoords: [52, 60],
      team: 'away',
    },
  ];
  
  // 传球数据
  const passes: Pass[] = [
    { from: 7, to: 9, type: 'long' }, // 克罗斯长传给维尼修斯
    { from: 9, to: 10, type: 'short' }, // 维尼修斯短传给本泽马
    { from: 6, to: 11, type: 'cross' }, // 卡马文加传中给罗德里戈
  ];
  
  return (
    <AbsoluteFill
      style={{
        backgroundColor: '#1a1a1a',
        overflow: 'hidden',
      }}
    >
      {/* 战术标题 */}
      <TacticsTitle frame={frame} fps={fps} />
      
      {/* 足球场地 + 球员 + 传球路线 */}
      <div
        style={{
          position: 'absolute',
          left: '5%',
          top: '22%',
          width: '90%',
          height: '74%',
        }}
      >
        <FootballPitch />
        
        {/* 传球路线 */}
        {passes.map((pass, index) => {
          const fromPlayer = [...homePlayers, ...awayPlayers].find(
            (p) => p.id === pass.from
          );
          const toPlayer = [...homePlayers, ...awayPlayers].find(
            (p) => p.id === pass.to
          );
          
          if (!fromPlayer || !toPlayer) return null;
          
          return (
            <PassLine
              key={index}
              from={fromPlayer}
              to={toPlayer}
              frame={frame}
              fps={fps}
              type={pass.type}
              delay={60 + index * 15}
            />
          );
        })}
        
        {/* 主队球员 */}
        {homePlayers.map((player, index) => (
          <PlayerMarker
            key={player.id}
            player={player}
            frame={frame}
            fps={fps}
            showTrail={player.endCoords !== undefined}
            delay={index * 3}
          />
        ))}
        
        {/* 客队球员 */}
        {awayPlayers.map((player, index) => (
          <PlayerMarker
            key={player.id}
            player={player}
            frame={frame}
            fps={fps}
            showTrail={false}
            delay={index * 3 + 2}
          />
        ))}
      </div>
    </AbsoluteFill>
  );
};

// ==================== Composition 配置 ====================

/**
 * 注册到 Root.tsx 的配置
 */
export const footballTacticsConfig = {
  id: 'FootballTactics',
  component: FootballTactics,
  durationInFrames: 150, // 5 秒 @ 30 FPS
  fps: 30,
  width: 1920,
  height: 1080,
};
